import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { completeConnectFromUrl } from '../lib/calendar-connect-ticket';
import { Layout, PageHeader, Container } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { 
  Clock, Globe, Type, CheckCircle, AlertCircle,
  Eye, EyeOff, Link2, Palette, CalendarDays
} from 'lucide-react';

interface FormData {
  title: string;
  slug: string;
  description: string;
  duration: number;
  bufferTime: number;
  timezone: string;
  isActive: boolean;
  isPublic: boolean;
  theme: 'light' | 'dark' | 'auto';
  color?: string;
  templateId?: string;
  projectId?: string;
}

interface EurekaProject {
  id: string;
  name: string;
}

interface MeetingTemplate {
  id: string;
  name: string;
  duration: number;
}

interface AvailabilityRule {
  id?: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

const PRESET_COLORS = [
  '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#4f46e5', '#000000'
];

const DURATIONS = [
  { value: 15, label: '15 min', icon: '⚡' },
  { value: 30, label: '30 min', icon: '⏱️' },
  { value: 45, label: '45 min', icon: '🕐' },
  { value: 60, label: '1 hour', icon: '🕑' },
  { value: 90, label: '1.5 hours', icon: '🕒' },
  { value: 120, label: '2 hours', icon: '🕓' },
];

const BUFFER_TIMES = [
  { value: 0, label: 'No buffer' },
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
];

export default function SchedulingForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState<FormData>({
    title: '',
    slug: '',
    description: '',
    duration: 30,
    bufferTime: 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    isActive: true,
    isPublic: true,
    theme: 'light',
    color: '#2563eb',
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState<MeetingTemplate[]>([]);
  const [projects, setProjects] = useState<EurekaProject[]>([]);

  const [showPreview, setShowPreview] = useState(false);
  const [availabilityRules, setAvailabilityRules] = useState<AvailabilityRule[]>([
    { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 2, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 3, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 4, startTime: '09:00', endTime: '17:00' },
    { dayOfWeek: 5, startTime: '09:00', endTime: '17:00' },
  ]);

  // Google sends the browser back here with ?connect_ticket=. The calendar is only
  // linked once our backend hands the ticket back as the signed-in user.
  const [calendarNotice, setCalendarNotice] = useState<'connected' | 'failed' | null>(null);
  useEffect(() => {
    void completeConnectFromUrl((ticket) => api.completeCalendarConnect(ticket)).then((result) => {
      if (result !== 'none') setCalendarNotice(result);
    });
  }, []);

  useEffect(() => {
    if (isEditing) {
      loadPage();
    }
    loadTemplates();
    loadProjects();
  }, [id]);

  const loadTemplates = async () => {
    try {
      const data = await api.getMeetingTemplates() as { templates: MeetingTemplate[] };
      setTemplates(data.templates || []);
    } catch {
      // Templates are optional - silently fail
    }
  };

  const loadProjects = async () => {
    try {
      const data = await api.getEurekaProjects();
      setProjects(data.projects || []);
    } catch {
      // Projects are optional - silently fail
    }
  };

  const loadPage = async () => {
    try {
      setLoading(true);
      const page = await api.getSchedulingPage(id!) as any;
      setFormData({
        title: page.title,
        slug: page.slug,
        description: page.description || '',
        duration: page.duration,
        bufferTime: page.bufferTime,
        timezone: page.timezone,
        isActive: page.isActive,
        isPublic: page.isPublic ?? true,
        theme: page.theme || 'light',
        color: page.color || '#2563eb',
        projectId: page.projectId || undefined,
      });
      
      // Load availability rules
      if (page.availabilityRules && page.availabilityRules.length > 0) {
        setAvailabilityRules(page.availabilityRules.map((r: any) => ({
          id: r.id,
          dayOfWeek: r.dayOfWeek,
          startTime: r.startTime,
          endTime: r.endTime,
        })));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');

      let pageId = id;

      if (isEditing) {
        await api.updateSchedulingPage(id!, formData);
      } else {
        const newPage = await api.createSchedulingPage(formData) as { id: string };
        pageId = newPage.id;
      }

      // Apply template if selected
      if (pageId && formData.templateId) {
        try {
          await api.applyTemplateToPage(formData.templateId, pageId);
        } catch (err) {
          console.error('Failed to apply template:', err);
        }
      }

      // Save availability rules
      if (pageId) {
        // First, delete existing rules if editing
        if (isEditing) {
          const page = await api.getSchedulingPage(pageId) as any;
          if (page.availabilityRules) {
            for (const rule of page.availabilityRules) {
              await api.deleteAvailabilityRule(pageId, rule.id);
            }
          }
        }

        // Add new rules
        for (const rule of availabilityRules) {
          await api.addAvailabilityRule(pageId, {
            dayOfWeek: rule.dayOfWeek,
            startTime: rule.startTime,
            endTime: rule.endTime,
          });
        }
      }

      navigate('/scheduling');
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const publicUrl = `${window.location.origin}/schedule/${formData.slug || 'your-page'}`;

  if (loading) {
    return (
      <Layout>
        <Container>
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title={isEditing ? 'Edit Scheduling Page' : 'Create Scheduling Page'}
        description={isEditing ? 'Update your page settings' : 'Set up a new booking page for your clients'}
        action={
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              leftIcon={showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            >
              {showPreview ? 'Hide Preview' : 'Preview'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/scheduling')}
            >
              Cancel
            </Button>
          </div>
        }
      />

      <Container>
        {calendarNotice === 'connected' && (
          <div role="status" className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            Calendar connected. New bookings will be added to your Google Calendar.
          </div>
        )}
        {calendarNotice === 'failed' && (
          <div role="alert" className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            Connection failed, try again. Your Google Calendar was not connected.
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        <div className={`grid gap-8 ${showPreview ? 'lg:grid-cols-2' : 'max-w-3xl mx-auto'}`}>
          {/* Form */}
          <div>
            <form onSubmit={handleSubmit}>
              <Tabs defaultValue="basic" className="space-y-6">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic">
                    <Type className="w-4 h-4 mr-2" />
                    Basic Info
                  </TabsTrigger>
                  <TabsTrigger value="settings">
                    <Clock className="w-4 h-4 mr-2" />
                    Settings
                  </TabsTrigger>
                  <TabsTrigger value="availability">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Availability
                  </TabsTrigger>
                  <TabsTrigger value="appearance">
                    <Palette className="w-4 h-4 mr-2" />
                    Appearance
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Page Information</CardTitle>
                      <CardDescription>
                        The basic details that will be displayed to people booking with you.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Page Title *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={(e) => {
                            const title = e.target.value;
                            setFormData({
                              ...formData,
                              title,
                              slug: !isEditing ? generateSlug(title) : formData.slug,
                            });
                          }}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                          placeholder="e.g., 30 Minute Discovery Call"
                        />
                        <p className="mt-2 text-sm text-gray-500">
                          This will be the main heading on your booking page.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL Slug *
                        </label>
                        <div className="flex rounded-xl shadow-sm">
                          <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                            {window.location.origin}/schedule/
                          </span>
                          <input
                            type="text"
                            required
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            className="flex-1 px-4 py-3 rounded-none rounded-r-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            placeholder="your-meeting"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          rows={4}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                          placeholder="Describe what this meeting is about and what attendees can expect..."
                        />
                      </div>

                      {projects.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Eureka Project (optional)
                          </label>
                          <select
                            value={formData.projectId || ''}
                            onChange={(e) => setFormData({ ...formData, projectId: e.target.value || undefined })}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                          >
                            <option value="">— No project —</option>
                            {projects.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          <p className="mt-2 text-sm text-gray-500">
                            Associate this page with an Eureka Labo project for activity logging.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Meeting Settings</CardTitle>
                      <CardDescription>
                        Configure the duration and timing options for bookings.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Meeting Duration *
                        </label>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                          {DURATIONS.map((duration) => (
                            <button
                              key={duration.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, duration: duration.value })}
                              className={`
                                p-4 rounded-xl border-2 text-center transition-all
                                ${formData.duration === duration.value 
                                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                }
                              `}
                            >
                              <div className="text-2xl mb-1">{duration.icon}</div>
                              <div className="font-medium">{duration.label}</div>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Buffer Time
                        </label>
                        <div className="flex flex-wrap gap-3">
                          {BUFFER_TIMES.map((buffer) => (
                            <button
                              key={buffer.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, bufferTime: buffer.value })}
                              className={`
                                px-4 py-2 rounded-full border-2 text-sm font-medium transition-all
                                ${formData.bufferTime === buffer.value 
                                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                  : 'border-gray-200 hover:border-blue-300'
                                }
                              `}
                            >
                              {buffer.label}
                            </button>
                          ))}
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Add buffer time between consecutive meetings.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Timezone
                        </label>
                        <div className="relative">
                          <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <select
                            value={formData.timezone}
                            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none bg-white"
                          >
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">Eastern Time (ET)</option>
                            <option value="America/Chicago">Central Time (CT)</option>
                            <option value="America/Denver">Mountain Time (MT)</option>
                            <option value="America/Los_Angeles">Pacific Time (PT)</option>
                            <option value="Europe/London">London (GMT)</option>
                            <option value="Europe/Paris">Paris (CET)</option>
                            <option value="Asia/Tokyo">Tokyo (JST)</option>
                            <option value="Asia/Singapore">Singapore (SGT)</option>
                            <option value="Australia/Sydney">Sydney (AEST)</option>
                          </select>
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-sm font-medium text-gray-900">
                              Page Status
                            </label>
                            <p className="text-sm text-gray-500">
                              {formData.isActive ? 'Page is accepting bookings' : 'Page is paused'}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                            className={`
                              relative inline-flex h-7 w-12 items-center rounded-full transition-colors
                              ${formData.isActive ? 'bg-green-500' : 'bg-gray-300'}
                            `}
                          >
                            <span
                              className={`
                                inline-block h-5 w-5 transform rounded-full bg-white transition-transform
                                ${formData.isActive ? 'translate-x-6' : 'translate-x-1'}
                              `}
                            />
                          </button>
                        </div>
                      </div>

                      {templates.length > 0 && (
                        <div className="border-t pt-6">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Apply Meeting Template (optional)
                          </label>
                          <select
                            value={formData.templateId || ''}
                            onChange={(e) => setFormData({ ...formData, templateId: e.target.value || undefined })}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                          >
                            <option value="">No template</option>
                            {templates.map(t => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.duration} min)
                              </option>
                            ))}
                          </select>
                          <p className="mt-2 text-sm text-gray-500">
                            Applying a template will configure questions, meeting links, and notifications.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="availability" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Weekly Availability</CardTitle>
                      <CardDescription>
                        Set your available hours for each day of the week. This is when people can book meetings with you.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                        const dayRules = availabilityRules.filter(r => r.dayOfWeek === day);
                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                        
                        return (
                          <div key={day} className="flex items-start space-x-4 py-3 border-b border-gray-100 last:border-0">
                            <div className="w-28 font-medium text-gray-700 pt-2">
                              {dayNames[day]}
                            </div>
                            
                            <div className="flex-1 space-y-2">
                              {dayRules.length === 0 ? (
                                <div className="text-sm text-gray-400 italic py-2">Not available</div>
                              ) : (
                                dayRules.map((rule, idx) => (
                                  <div key={idx} className="flex items-center space-x-2">
                                    <input
                                      type="time"
                                      value={rule.startTime}
                                      onChange={(e) => {
                                        const newRules = [...availabilityRules];
                                        const ruleIndex = newRules.findIndex(r => 
                                          r.dayOfWeek === day && r.startTime === rule.startTime && r.endTime === rule.endTime
                                        );
                                        if (ruleIndex >= 0) {
                                          newRules[ruleIndex].startTime = e.target.value;
                                          setAvailabilityRules(newRules);
                                        }
                                      }}
                                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-500">to</span>
                                    <input
                                      type="time"
                                      value={rule.endTime}
                                      onChange={(e) => {
                                        const newRules = [...availabilityRules];
                                        const ruleIndex = newRules.findIndex(r => 
                                          r.dayOfWeek === day && r.startTime === rule.startTime && r.endTime === rule.endTime
                                        );
                                        if (ruleIndex >= 0) {
                                          newRules[ruleIndex].endTime = e.target.value;
                                          setAvailabilityRules(newRules);
                                        }
                                      }}
                                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAvailabilityRules(availabilityRules.filter(r => 
                                          !(r.dayOfWeek === day && r.startTime === rule.startTime && r.endTime === rule.endTime)
                                        ));
                                      }}
                                      className="text-red-500 hover:text-red-700 text-sm px-2"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))
                              )}
                              
                              <button
                                type="button"
                                onClick={() => {
                                  setAvailabilityRules([...availabilityRules, { 
                                    dayOfWeek: day, 
                                    startTime: '09:00', 
                                    endTime: '17:00' 
                                  }]);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                + Add time slot
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="appearance" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Appearance</CardTitle>
                      <CardDescription>
                        Customize how your booking page looks.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Theme Color
                        </label>
                        <div className="flex flex-wrap gap-3">
                          {PRESET_COLORS.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setFormData({ ...formData, color })}
                              className={`
                                w-10 h-10 rounded-full border-2 transition-all
                                ${formData.color === color 
                                  ? 'border-gray-900 scale-110' 
                                  : 'border-gray-200 hover:border-gray-400'
                                }
                              `}
                              style={{ backgroundColor: color }}
                            >
                              {formData.color === color && (
                                <CheckCircle className="w-5 h-5 text-white mx-auto" />
                              )}
                            </button>
                          ))}
                          <input
                            type="color"
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="w-10 h-10 rounded-full border-2 border-gray-200 cursor-pointer"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Theme Mode
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { value: 'light', label: 'Light', icon: '☀️' },
                            { value: 'dark', label: 'Dark', icon: '🌙' },
                            { value: 'auto', label: 'Auto', icon: '🔄' },
                          ].map((theme) => (
                            <button
                              key={theme.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, theme: theme.value as any })}
                              className={`
                                p-4 rounded-xl border-2 text-center transition-all
                                ${formData.theme === theme.value 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-blue-300'
                                }
                              `}
                            >
                              <div className="text-2xl mb-1">{theme.icon}</div>
                              <div className="font-medium text-sm">{theme.label}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/scheduling')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={saving}
                    leftIcon={<CheckCircle className="w-4 h-4" />}
                  >
                    {isEditing ? 'Save Changes' : 'Create Page'}
                  </Button>
                </div>
              </Tabs>
            </form>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="lg:sticky lg:top-8">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-gray-500">Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="rounded-xl overflow-hidden"
                    style={{ 
                      background: formData.color 
                        ? `linear-gradient(135deg, ${formData.color}, ${formData.color}dd)` 
                        : '#2563eb' 
                    }}
                  >
                    <div className="p-6 text-white">
                      <h3 className="text-xl font-bold mb-2">
                        {formData.title || 'Your Meeting Title'}
                      </h3>
                      <p className="text-white/80 text-sm mb-4">
                        {formData.description || 'Description will appear here...'}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-sm text-white/90">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {formData.duration} min
                        </span>
                        <span className="flex items-center">
                          <Globe className="w-4 h-4 mr-1" />
                          {formData.timezone.split('/')[1]?.replace('_', ' ') || formData.timezone}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Link2 className="w-4 h-4 mr-2" />
                      Public URL
                    </div>
                    <div className="text-sm font-medium text-gray-900 break-all">
                      {publicUrl}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge variant={formData.isActive ? 'success' : 'default'}>
                        {formData.isActive ? 'Active' : 'Paused'}
                      </Badge>
                      <Badge variant="default">
                        {formData.isPublic ? 'Public' : 'Private'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
}
